import React, { useState, useEffect, useCallback, useRef } from 'react';
import { searchSpecies, searchArchivedSpecies, getSpecies } from '../services/speciesService';
import SpeciesEditorModal from '../components/harvesting/SpeciesEditorModal';
import SpeciesInfoModal from '../components/harvesting/SpeciesInfoModal';
import SpeciesImport from '../components/harvesting/SpeciesImport';
import useSpeciesAccess from '../hooks/useSpeciesAccess';

import { SPECIES_CATEGORIES as CATEGORIES } from '../utils/speciesCategories';

const HARVEST_MAP = {
  fishing: '🎣 Fishing',
  hunting: '🦌 Hunting',
  birds:   '🦅 Birds',
  plants_medicine: '🌿 Plants & Medicine',
  environmental: '⚠️ Environmental',
  other: '📍 Other',
};

const CAT_ICONS = {
  Fish: '🐟', Mammal: '🦌', Bird: '🦅', Reptile: '🐢', Amphibian: '🐸',
  Tree: '🌲', Plant: '🌿', Berry: '🫐', Fungus: '🍄', Medicine: '💊',
  'Aquatic Resource': '💧', 'General Resource': '📦', 'Environmental Observation': '⚠️',
};

function useDebounce(fn, delay) {
  const t = useRef(null);
  useEffect(() => () => clearTimeout(t.current), [fn]);
  return useCallback((...args) => {
    clearTimeout(t.current);
    t.current = setTimeout(() => fn(...args), delay);
  }, [fn, delay]);
}

export default function SpeciesListPage() {
  const { canEdit, canArchive } = useSpeciesAccess();

  const [archived, setArchived] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [query, setQuery]         = useState('');
  const [category, setCategory]   = useState('');
  const [results, setResults]     = useState([]);
  const [loading, setLoading]     = useState(false);
  const [modalSpecies, setModal]  = useState(null);
  const [editing, setEditing]     = useState(null); // null | 'new' | species object
  const [total, setTotal]         = useState(0);
  const searchGeneration = useRef(0);
  const editSpecies = async (species) => {
    try {
      const response = await getSpecies(species._id);
      setModal(null);
      setEditing(response.data);
    } catch (err) {
      setSearchError(err.response?.data?.message || 'Could not load species details. Please retry.');
    }
  };

  const doSearch = useCallback(async (q, cat) => {
    const generation = ++searchGeneration.current;
    setLoading(true);
    setSearchError('');
    try {
      const search = archived && canArchive ? searchArchivedSpecies : searchSpecies;
      const res = await search(q || '', { category: cat || undefined, limit: 50 });
      if (generation === searchGeneration.current) {
        setResults(res.data || []);
        setTotal(res.data?.length || 0);
      }
    } catch (err) {
      if (generation === searchGeneration.current) {
        setResults([]);
        setSearchError(err.response?.data?.message || 'Could not load species. Please retry.');
      }
    } finally {
      if (generation === searchGeneration.current) setLoading(false);
    }
  }, [archived, canArchive]);

  const debounced = useDebounce(doSearch, 280);

  useEffect(() => { doSearch(query, category); }, [doSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleQuery = (v) => {
    setQuery(v);
    debounced(v, category);
  };

  const handleCategory = (cat) => {
    const next = cat === category ? '' : cat;
    setCategory(next);
    doSearch(query, next);
  };

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 20px' }}>
      <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 4px', color: '#1e293b' }}>
            🔬 Species Catalog
          </h1>
          <p style={{ margin: 0, fontSize: 14, color: '#64748b' }}>
            Master shared species catalogue for guided harvest logging and stewardship across communities. Click a species to see its identity, aliases and classification; community area guidance is managed separately in Stewardship.
          </p>
        </div>
        {canEdit && (
          <button onClick={() => setEditing('new')}>+ Add Species</button>
        )}
      </div>

      {canEdit && <SpeciesImport onImported={() => doSearch(query, category)} />}
      {canArchive && <label>Catalogue status
        <select value={archived ? 'archived' : 'active'} onChange={(e) => setArchived(e.target.value === 'archived')}>
          <option value="active">Active species</option>
          <option value="archived">Archived species</option>
        </select>
      </label>}
      {searchError && <p role="alert">{searchError}</p>}

      {/* Search bar */}
      <div style={{ marginBottom: 14 }}>
        <input
          value={query}
          onChange={(e) => handleQuery(e.target.value)}
          placeholder="Search by name, alias or community name…"
          style={{
            width: '100%', padding: '10px 14px', fontSize: 14, boxSizing: 'border-box',
            border: '1px solid #d1d5db', borderRadius: 8,
          }}
          autoComplete="off"
        />
      </div>

      {/* Category filter chips */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
        {CATEGORIES.map((cat) => (
          <button
            key={cat}
            type="button"
            onClick={() => handleCategory(cat)}
            style={{
              padding: '4px 12px', fontSize: 12, borderRadius: 999, cursor: 'pointer',
              border: category === cat ? '2px solid #2563eb' : '1px solid #cbd5e1',
              background: category === cat ? '#eff6ff' : '#f8fafc',
              color: category === cat ? '#1d4ed8' : '#475569',
              fontWeight: category === cat ? 700 : 400,
            }}
          >
            {CAT_ICONS[cat] || ''} {cat}
          </button>
        ))}
        {category && (
          <button
            type="button"
            onClick={() => handleCategory('')}
            style={{
              padding: '4px 12px', fontSize: 12, borderRadius: 999, cursor: 'pointer',
              border: '1px solid #fca5a5', background: '#fef2f2', color: '#b91c1c',
            }}
          >
            ✕ Clear filter
          </button>
        )}
      </div>

      {/* Status line */}
      <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 12 }}>
        {loading ? 'Searching…' : `${total} ${total === 1 ? 'species' : 'species'} found`}
        {category && ` in ${category}`}
        {query && ` matching "${query}"`}
      </div>

      {/* Results grid */}
      {!loading && results.length === 0 && (
        <div style={{ textAlign: 'center', color: '#94a3b8', padding: '40px 0', fontSize: 14 }}>
          No species found. Try a different search or clear the filter.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 }}>
        {results.map((s) => (
          <div
            key={s._id}
            onClick={() => setModal(s)}
            style={{
              border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 14px',
              background: '#fff', cursor: 'pointer', transition: 'box-shadow .12s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,.10)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 6 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b', flex: 1 }}>
                {CAT_ICONS[s.category] || '📍'} {s.commonName}
              </div>
              <span style={{
                fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
                background: '#f1f5f9', color: '#475569', flexShrink: 0, whiteSpace: 'nowrap',
              }}>
                {s.category}
              </span>
              {canEdit && (
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  style={{ flexShrink: 0, padding: '2px 8px', fontSize: 11 }}
                  onClick={(e) => { e.stopPropagation(); editSpecies(s); }}
                >
                  Edit
                </button>
              )}
            </div>

            {s.communityName && (
              <div style={{ fontSize: 12, color: '#16a34a', fontStyle: 'italic', marginTop: 3 }}>
                {s.communityName}
              </div>
            )}

            {s.alternateNames?.length > 0 && (
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>
                Also: {s.alternateNames.slice(0, 3).join(', ')}
                {s.alternateNames.length > 3 && ' …'}
              </div>
            )}

            {s.harvestCategory && (
              <div style={{ marginTop: 6, fontSize: 11, color: '#64748b' }}>
                {HARVEST_MAP[s.harvestCategory] || s.harvestCategory}
              </div>
            )}

            {s.isGeneralResource && (
              <div style={{ marginTop: 4 }}>
                <span style={{ fontSize: 10, background: '#f0fdf4', color: '#166534', padding: '1px 6px', borderRadius: 4 }}>
                  General resource
                </span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Species info modal */}
      {modalSpecies && (
        <SpeciesInfoModal
          species={modalSpecies}
          speciesId={modalSpecies._id}
          onClose={() => setModal(null)}
          onEdit={canEdit ? editSpecies : undefined}
        />
      )}

      {/* Species editor (staff/admin) */}
      {editing && (
        <SpeciesEditorModal
          species={editing === 'new' ? null : editing}
          canArchive={canArchive}
          archived={editing?.isActive === false}
          onClose={() => setEditing(null)}
          onSaved={() => {
            doSearch(query, category);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}
